import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, StatusDot } from "@/components/Status";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Building2, Plus, ArrowUpRight, Trash2, AlertTriangle } from "lucide-react";

const empty = {
  name: "",
  sip_domain: "",
  sip_server_id: "",
  city: "",
  address: "",
  extension_prefix: "1",
  voice_vlan: "",
};

export default function Hotels() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isSuper = user?.role === "super_admin";
  const [hotels, setHotels] = useState([]);
  const [servers, setServers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // hotel object
  const [rebootCounts, setRebootCounts] = useState({}); // {hotelId: nb postes à rebooter}

  const load = () =>
    api.get("/hotels").then((r) => {
      setHotels(r.data);
      // Alerting proactif : récupère le nb de postes à redémarrer par hôtel (en
      // parallèle) pour afficher un badge sans entrer dans chaque hôtel.
      r.data.forEach((h) => {
        api.get(`/hotels/${h.id}/phones-to-reboot`)
          .then((rr) => setRebootCounts((c) => ({ ...c, [h.id]: rr.data?.count || 0 })))
          .catch(() => {});
      });
    });
  useEffect(() => {
    load();
    if (isSuper) api.get("/servers").then((r) => setServers(r.data));
  }, [isSuper]);

  const deleteHotel = async () => {
    try {
      await api.delete(`/hotels/${confirmDelete.id}`);
      toast.success(`${confirmDelete.name} supprimé`);
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/hotels", form);
      toast.success("Hôtel créé");
      setOpen(false);
      setForm(empty);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="hotels-page">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
            Tenants
          </p>
          <h1 className="mt-1 font-head text-3xl font-black tracking-tighter text-zinc-900">
            Hôtels
          </h1>
        </div>
        {isSuper && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-hotel-btn" className="rounded-sm bg-blue-600 hover:bg-blue-700">
                <Plus className="mr-1 h-4 w-4" /> Nouvel hôtel
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader>
                <DialogTitle className="font-head tracking-tight">Créer un hôtel (tenant)</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3 py-2">
                <div className="col-span-2">
                  <Lbl>Nom de l'hôtel</Lbl>
                  <Input data-testid="hotel-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm text-sm" placeholder="Nom de l'hôtel" />
                </div>
                <div className="col-span-2">
                  <Lbl>Domaine SIP</Lbl>
                  <Input data-testid="hotel-domain-input" value={form.sip_domain} onChange={(e) => setForm({ ...form, sip_domain: e.target.value })} className="mt-1 rounded-sm font-mono text-sm" placeholder="lutece.hotelsip.io" />
                </div>
                <div className="col-span-2">
                  <Lbl>Serveur SIP</Lbl>
                  <Select value={form.sip_server_id} onValueChange={(v) => setForm({ ...form, sip_server_id: v })}>
                    <SelectTrigger data-testid="hotel-server-select" className="mt-1 rounded-sm text-sm">
                      <SelectValue placeholder="Sélectionner un serveur" />
                    </SelectTrigger>
                    <SelectContent>
                      {servers.map((s) => (
                        <SelectItem key={s.id} value={s.id} data-testid={`server-option-${s.id}`}>
                          {s.name} — {s.ip}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Lbl>Ville</Lbl>
                  <Input data-testid="hotel-city-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1 rounded-sm text-sm" placeholder="Paris" />
                </div>
                <div>
                  <Lbl>Préfixe extension</Lbl>
                  <Input data-testid="hotel-prefix-input" value={form.extension_prefix} onChange={(e) => setForm({ ...form, extension_prefix: e.target.value })} className="mt-1 rounded-sm font-mono text-sm" placeholder="1" />
                </div>
                <div>
                  <Lbl>VLAN voix (provisioning)</Lbl>
                  <Input data-testid="hotel-vlan-input" value={form.voice_vlan} onChange={(e) => setForm({ ...form, voice_vlan: e.target.value })} className="mt-1 rounded-sm font-mono text-sm" placeholder="ex. 200" />
                </div>
              </div>
              <DialogFooter>
                <Button
                  data-testid="save-hotel-btn"
                  onClick={save}
                  disabled={saving || !form.name || !form.sip_domain || !form.sip_server_id}
                  className="rounded-sm bg-blue-600 hover:bg-blue-700"
                >
                  Créer l'hôtel
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {hotels.map((h) => (
          <div
            key={h.id}
            data-testid={`hotel-card-${h.id}`}
            onClick={() => navigate(`/hotels/${h.id}`)}
            className="group cursor-pointer rounded-sm border border-zinc-200 bg-white p-5 transition-colors hover:border-blue-300"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-zinc-400" />
                <span className="font-head font-bold tracking-tight text-zinc-900">{h.name}</span>
                {rebootCounts[h.id] > 0 && (
                  <span
                    title={`${rebootCounts[h.id]} poste(s) à redémarrer`}
                    className="inline-flex items-center gap-1 rounded-sm bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700"
                  >
                    <AlertTriangle className="h-3 w-3" />
                    {rebootCounts[h.id]}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {isSuper && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setConfirmDelete(h); }}
                    className="hidden group-hover:flex items-center justify-center rounded-sm p-1 text-zinc-300 hover:bg-red-50 hover:text-red-500 transition-colors"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
                <ArrowUpRight className="h-4 w-4 text-zinc-300 transition-colors group-hover:text-blue-600" />
              </div>
            </div>
            <div className="mt-1 font-mono text-xs text-zinc-500">{h.sip_domain}</div>
            <div className="mt-4 flex items-center justify-between">
              {h.contact_mode === "qr" ? (
                <span className="inline-flex items-center gap-1.5 font-mono text-xs text-zinc-700">
                  {h.rooms_count} chambres
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 font-mono text-xs text-zinc-700">
                  <StatusDot online={h.rooms_online > 0} />
                  {h.rooms_online}/{h.rooms_count} chambres
                </span>
              )}
              <StatusBadge status={h.trunk_status} />
            </div>
            <div className="mt-3 border-t border-zinc-100 pt-2 font-mono text-[11px] text-zinc-400">
              {h.server_name} · {h.city || "—"}
            </div>
          </div>
        ))}
        {hotels.length === 0 && (
          <div className="col-span-full rounded-sm border border-dashed border-zinc-300 bg-white py-12 text-center text-sm text-zinc-400">
            Aucun hôtel. {isSuper && "Créez votre premier tenant."}
          </div>
        )}
      </div>

      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent className="rounded-sm max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-head tracking-tight">Supprimer l'hôtel</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-600">
            Supprimer <span className="font-semibold">{confirmDelete?.name}</span> ? Cette action supprimera aussi toutes les chambres, trunks et configurations associés.
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-sm" onClick={() => setConfirmDelete(null)}>
              Annuler
            </Button>
            <Button className="rounded-sm bg-red-600 hover:bg-red-700" onClick={deleteHotel}>
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Lbl({ children }) {
  return (
    <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
      {children}
    </Label>
  );
}
